import { AxiosError } from "axios"
import * as Sentry from "@sentry/react"

type EndpointRule =
  | { type: "exact"; path: string }
  | { type: "prefix"; path: string }
  | { type: "regex"; pattern: RegExp }

const IGNORED_ENDPOINTS: EndpointRule[] = [
  // *** EXTRACT ***
  { type: "exact", path: "/daily-rewards" },
  { type: "exact", path: "/collect-coin" },
  // { type: "exact", path: "/boss-clan-buildings/buildings" },
  // *** PREFIX ***
  { type: "prefix", path: "/elixirs/elixirs-instances/count" },
  { type: "prefix", path: "/favorite-items/check" },
  { type: "prefix", path: "/quests/complete/" }
]

const IGNORED_MESSAGES: string[] = [
  "Cannot read properties of null (reading 'tagName')",
  "null is not an object (evaluating 'e.tagName')",
  "Load failed"
]

/** Get request path from Axios config (path-only, without baseURL or query) */
const getRequestPath = (config: AxiosError["config"]): string => {
  if (!config?.url) return ""

  const rawUrl = config.url

  if (rawUrl.startsWith("http")) {
    try {
      return new URL(rawUrl).pathname
    } catch {
      return rawUrl.split("?")[0]
    }
  }

  // For relative URLs we treat them as path-only and strip query params
  return rawUrl.split("?")[0]
}

const isIgnoredEndpoint = (requestPath: string): boolean => {
  if (!requestPath) return false
  for (const rule of IGNORED_ENDPOINTS) {
    if (rule.type === "exact" && requestPath === rule.path) return true
    if (rule.type === "prefix" && requestPath.includes(rule.path)) return true
    if (rule.type === "regex" && rule.pattern.test(requestPath)) return true
  }
  return false
}

const getUrlFromFullUrl = (fullUrl: string): string => {
  return new URL(fullUrl).pathname.replace("/api/v1", "")
}

/** API response messages to ignore (substring match). */
const IGNORED_RESPONSE_MESSAGES: string[] = [
  "Failed to authenticate token",
  "Cannot buy a subscription with default tool"
]

const isIgnoredResponseMessage = (message: string | undefined): boolean =>
  IGNORED_RESPONSE_MESSAGES.some((m) => (message ?? "").includes(m))

/** HTTP status codes to never send to Sentry (e.g. 401, 403, 404). */
const IGNORED_HTTP_STATUSES: number[] = []

/** Do not send network errors (no response) to Sentry. */
const IGNORE_NETWORK_ERRORS = true

/** Global error message patterns Sentry will not send. Supports globs (e.g. "*timeout*"). */
const IGNORED_ERROR_PATTERNS: string[] = [
  "Unexpected identifier",
  "Cannot access",
  "useModal must be used within a ModalProvider",
  "useModals must be used within a ModalProvider",
  "[TON_CONNECT_SDK_ERROR]",
  "Cannot read properties of null (reading 'parentNode')",
  "Unable to retrieve launch parameters from any known source.",
  "Cannot read properties of undefined (reading 'WebApp')",
  "timeout of",
  "Network Error",
  "Failed to authenticate token",
  "amazonaws",
  "AbortError: The operation was aborted.",
  "is not a valid JavaScript MIME type",
  "Importing a module script failed",
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module"
]

/**
 * Normalize URL for fingerprinting by replacing dynamic segments
 * e.g., /users/123/orders/456 -> /users/:id/orders/:id
 */
const normalizeUrl = (url: string): string => {
  // Remove query params for grouping
  const urlWithoutQuery = url.split("?")[0]

  // Replace numeric IDs with :id placeholder
  // Matches: /123, /456abc (UUIDs partial), etc.
  const normalized = urlWithoutQuery
    // 1. UUIDs first (contain hyphens, no overlap with others)
    .replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      "/:uuid"
    )
    // 2. MongoDB ObjectIds (24 hex chars) — before numeric, so full segment is replaced
    .replace(/\/[0-9a-f]{24}/gi, "/:id")
    // 3. Numeric IDs last (e.g. /123, /456)
    .replace(/\/\d+/g, "/:id")

  return normalized
}

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Should be called as early as possible in the application lifecycle
 */
export const initSentry = () => {
  const dns = import.meta.env.VITE_SENTRY_DSN
  const mode = import.meta.env.VITE_NODE_ENV
  const version = import.meta.env.VITE_APP_VERSION
  // Skip initialization in development for cleaner console
  if (!dns) {
    console.log("[Sentry] DSN not configured, skipping initialization")
    return
  }

  if (!["local", "development", "staging", "production"].includes(mode)) {
    console.log("[Sentry] Mode not configured, skipping initialization")
    return
  }

  console.log("[Sentry] Mode:", mode)

  // const isLocal = mode === "local"

  // if (isLocal) {
  //   return
  // }

  // Sentry.init({
  //   dsn: dns,
  //   // Setting this option to true will send default PII data to Sentry.
  //   // For example, automatic IP address collection on events
  //   sendDefaultPii: true,
  // })

  Sentry.init({
    dsn: dns,
    sendDefaultPii: true,
    environment: mode,
    release: version,

    // Integrations for enhanced error context
    integrations: [
      // Browser Tracing for performance monitoring
      Sentry.browserTracingIntegration({
        enableLongTask: true,
        enableInp: true,
        traceFetch: true,
        traceXHR: true,
        shouldCreateSpanForRequest: (url) => {
          return url.includes("/api/")
        }
      }),
      // Session Replay for debugging
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: false,
        mask: [],
        block: [],

        networkDetailAllowUrls: [/\/api\//],
        networkRequestHeaders: ["Content-Type", "Authorization"],
        networkResponseHeaders: ["Content-Type"],
        networkCaptureBodies: true
      })
    ],

    maxBreadcrumbs: 50,

    // Performance Monitoring — 100% бо Sentry вмикається ТІЛЬКИ для debug-юзерів (whitelist).
    tracesSampleRate: 1.0,

    // Session Replay — 100% бо Sentry вмикається ТІЛЬКИ для debug-юзерів (whitelist).
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,

    ignoreErrors: IGNORED_ERROR_PATTERNS,

    beforeSendLog(log) {
      console.log("[Sentry] log →", log)
      return log
    },

    beforeSendMetric(metric) {
      console.log("[Sentry] metric →", metric)
      return metric
    },

    beforeBreadcrumb(breadcrumb) {
      // console.log("[Sentry] breadcrumb →", breadcrumb, hint)

      const rawUrl = breadcrumb?.data?.url

      if (rawUrl) {
        const url = getUrlFromFullUrl(rawUrl)

        if (isIgnoredEndpoint(url)) {
          console.log("[Sentry] skip breadcrumb →", url)
          return null
        }
      }
      return breadcrumb
    },

    beforeSendSpan(span) {
      // console.log("[Sentry] span →", span)
      return span
    },

    beforeSendTransaction(event, hint) {
      console.log("[Sentry] transaction →", event, hint)
      return event
    },

    // Custom beforeSend hook for additional filtering/enrichment
    beforeSend(event, hint) {
      const error = hint.originalException as AxiosError

      console.log("[Sentry] error →", error)

      // Handle Axios errors specially
      if (error?.config?.url) {
        const method = error.config.method?.toUpperCase() || "UNKNOWN"
        const url = normalizeUrl(error.config.url)
        const status = error.response?.status ?? "NETWORK"
        const short = `${method} ${status} ${url}`

        const responseMessage = (error?.response?.data as { message?: string })
          ?.message
        const isResponseIgnored = isIgnoredResponseMessage(responseMessage)

        const requestPath = getRequestPath(error.config)
        const isEndpointIgnored = isIgnoredEndpoint(requestPath)

        if (isResponseIgnored) {
          console.log("[Sentry] skip msg →", short)
          return null
        }

        if (isEndpointIgnored) {
          console.log("[Sentry] skip endpoint →", short)
          return null
        }

        if (status === "NETWORK") {
          if (IGNORE_NETWORK_ERRORS) {
            console.log("[Sentry] skip network →", `${method} ${requestPath}`)
            return null
          }
        } else if (
          typeof status === "number" &&
          IGNORED_HTTP_STATUSES.includes(status)
        ) {
          console.log("[Sentry] skip status →", short)
          return null
        }

        console.log("[Sentry] send →", short)

        // Set fingerprint to group by method + endpoint + status
        event.fingerprint = ["{{ default }}", method, url, String(status)]

        // Add useful context
        event.tags = {
          ...event.tags,
          api_endpoint: url,
          api_method: method,
          http_status: String(status)
        }

        // Improve error title for better readability
        if (event.exception?.values?.[0]) {
          event.exception.values[0].value = short
        }
      } else {
        const msg =
          event.exception?.values?.[0]?.value ??
          (error as Error)?.message ??
          "non-Axios"

        if (IGNORED_MESSAGES.includes(msg)) {
          console.log("[Sentry] skip message →", msg)
          return null
        }

        console.log("[Sentry] non-Axios send →", msg)
      }

      // if (isLocal) {
      //   return null
      // }

      return event
    }

    // Custom beforeBreadcrumb hook to filter/modify breadcrumbs
    // beforeBreadcrumb(breadcrumb) {
    //   // Filter out noisy console logs
    //   if (breadcrumb.category === "console" && breadcrumb.level === "log") {
    //     return null
    //   }
    //   return breadcrumb
    // },
  })

  console.log("[Sentry] Initialized successfully")
}

/**
 * Set user context for error tracking
 * Call this after user authentication
 */
export const setSentryUser = (
  userId: string | number,
  username?: string,
  additionalData?: Record<string, string>
) => {
  Sentry.setUser({
    id: String(userId),
    username,
    ...additionalData
  })
}

/**
 * Clear user context (e.g., on logout)
 */
export const clearSentryUser = () => {
  Sentry.setUser(null)
}

/**
 * Add custom context for debugging
 */
export const setSentryContext = (
  name: string,
  data: Record<string, unknown>
) => {
  Sentry.setContext(name, data)
}

/**
 * Add a breadcrumb for additional context
 */
export const addSentryBreadcrumb = (
  message: string,
  category: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = "info"
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level
  })
}

/**
 * Manually capture an exception
 */
export const captureException = (
  error: Error | unknown,
  context?: Record<string, unknown>
) => {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setExtras(context)
      Sentry.captureException(error)
    })
  } else {
    Sentry.captureException(error)
  }
}

/**
 * Manually capture a message
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = "info"
) => {
  Sentry.captureMessage(message, level)
}

// Re-export Sentry's ErrorBoundary for convenience
export { ErrorBoundary as SentryErrorBoundary } from "@sentry/react"
